/**
 * Projection engine for the Advanced Retirement Calculator
 * 
 * This module contains the deterministic and Monte Carlo projection functions.
 * 
 * @module domain/retirement/projection
 */

import { AssetCategory, RetirementGoalKind, DEFAULT_MARGINAL_TAX_RATE, DEBT_ANNUAL_COST_RATE, SellType } from "./constants";
import type {
  AdvancedRetirementInputs,
  MonteCarloResult,
  PensionEarlyKitzbaStream,
  ProductBreakdown,
  ProductBalance,
  ProjectionResult,
  ProjectionYearPoint,
  RetirementGoal,
  GoalResult,
} from "./types";
import { calculateKitzbaTax, calculateReducedContribution, PENSION_TAX } from "./pensionLaw";
import {
  initializeCategoryBalances,
  totalCategoryBalances,
  withdrawProportionally,
  applyReturnsPerCategory,
  calculateStartingPortfolio,
  calculateWeightedReturns,
  // Product-level functions
  initializeProductBalances,
  totalProductBalances,
  productBalancesToCategoryBalances,
  applyReturnsPerProduct,
  calculateWeightedReturnsFromProducts,
  withdrawFromProductsProportionally,
  createSyntheticProduct,
} from "./portfolio";
import {
  calculateDetailedLifeEventsImpact,
  normalizeLifeEventEndYears,
  processSellRequests,
  processBuyFromCredit,
  processSellRequestsFromProducts,
  processBuyFromCreditToProducts,
} from "./lifeEvents";
import { sampleAnnualReturn, findProfileById, DEFAULT_PROFILE_BY_CATEGORY, type RiskProfile } from "./riskProfiles";

// ============================================================================
// PARTNER PENSION POOL (couple support)
// ============================================================================

/**
 * A self-contained pension pool for the second partner in a couple. It mirrors
 * the primary partner's pension machinery but in a simplified form (no early
 * kitzba / early-sell overrides). It accumulates the partner's locked pension
 * from salary-based contributions that stop when the partner retires, and
 * unlocks at the partner's own eligibility age producing a monthly kitzba
 * income plus a one-time lump sum.
 */
class PartnerPensionPool {
  private locked: number;
  private released = false;
  private activeMonthly = 0;
  private readonly expectedBalance: number;
  private readonly monthlyKitzba: number;
  private readonly lumpSumBase: number;

  constructor(
    private readonly params: {
      /** Partner age at which contributions stop (partner retires) */
      retirementAge: number;
      /** Partner age at which the pension unlocks */
      eligibilityAge: number;
      /** Partner's current monthly salary (drives contributions) */
      monthlyIncome: number;
      /** Contribution as a fraction of the partner's salary */
      contributionRate?: number;
      /** Initial locked pension balance (base currency) */
      initialLocked: number;
      /** Expected accumulated balance at eligibility (static, for scaling) */
      expectedBalance: number;
      /** Aggregate monthly kitzba at eligibility */
      monthlyKitzba: number;
      /** Aggregate net lump sum at eligibility */
      lumpSum: number;
      /** Annual pension return (decimal) */
      pensionReturn: number;
    },
  ) {
    this.locked = params.initialLocked;
    this.expectedBalance = params.expectedBalance;
    this.monthlyKitzba = params.monthlyKitzba;
    this.lumpSumBase = params.lumpSum;
  }

  /** Current locked balance (for display of combined locked pension). */
  get lockedBalance(): number {
    return this.locked;
  }

  /**
   * Advance the pool by one year.
   * @param partnerAge The partner's age this year
   * @param year Calendar year (for kitzba tax)
   * @param inflationFactor Inflation multiplier for this year
   * @param incomeReductionRatio Household income-reduction ratio (0-1)
   * @returns Net annual kitzba income and any lump sum to add to the portfolio.
   */
  step(
    partnerAge: number,
    year: number,
    inflationFactor: number,
    incomeReductionRatio: number,
  ): { netIncome: number; lumpSum: number; monthlyIncome: number } {
    let lumpSum = 0;

    if (!this.released && partnerAge >= this.params.eligibilityAge) {
      const scale =
        this.params.contributionRate != null && this.expectedBalance > 0
          ? this.locked / this.expectedBalance
          : 1;
      this.activeMonthly = this.monthlyKitzba * scale;
      lumpSum = this.lumpSumBase * scale;
      this.released = true;
      this.locked = 0;
    }

    if (this.released) {
      const inflatedMonthly = this.activeMonthly * inflationFactor;
      const taxResult = calculateKitzbaTax(inflatedMonthly, year, DEFAULT_MARGINAL_TAX_RATE);
      return {
        netIncome: taxResult.netPension * 12,
        lumpSum,
        monthlyIncome: inflatedMonthly,
      };
    }

    // Accumulation: add salary-based contributions while the partner works.
    let contrib = 0;
    if (partnerAge < this.params.retirementAge && this.params.contributionRate != null) {
      const effectiveIncome = calculateReducedContribution(this.params.monthlyIncome, incomeReductionRatio);
      contrib = effectiveIncome * this.params.contributionRate * 12;
    }
    this.locked = (this.locked + contrib) * (1 + this.params.pensionReturn);
    return { netIncome: 0, lumpSum: 0, monthlyIncome: 0 };
  }
}

/**
 * Build a PartnerPensionPool from inputs if the household is a couple with
 * partner pension data. Returns null in single-person mode.
 */
function createPartnerPensionPool(
  inputs: AdvancedRetirementInputs,
  initialLocked: number,
): PartnerPensionPool | null {
  const partner = inputs.partner;
  if (!partner) return null;
  const eligibilityAge = partner.pensionEligibilityAge ?? partner.retirementAge;
  const monthlyKitzba = partner.pensionCashflowAtRetirement ?? 0;
  const lumpSum = partner.pensionLumpSumAtRetirement ?? 0;
  const expectedBalance = partner.expectedPensionAtRetirement ?? 0;
  // Nothing to model if the partner has no pension at all.
  if (initialLocked <= 0 && monthlyKitzba <= 0 && lumpSum <= 0 && expectedBalance <= 0) {
    return null;
  }
  return new PartnerPensionPool({
    retirementAge: partner.retirementAge,
    eligibilityAge,
    monthlyIncome: partner.monthlyIncome ?? 0,
    contributionRate: partner.pensionContributionRate,
    initialLocked,
    expectedBalance,
    monthlyKitzba,
    lumpSum,
    pensionReturn: inputs.returnAssumptions[AssetCategory.PENSION].expectedReturn,
  });
}

// ============================================================================
// DETERMINISTIC PROJECTION
// ============================================================================

/**
 * Run deterministic projection with per-product tracking, pension lock/unlock, and buying credit
 * Uses product-level tracking for accurate per-product yield calculations
 */
export function runDeterministicProjection(
  inputs: AdvancedRetirementInputs,
  startingPortfolio: number,
  productBreakdowns?: ProductBreakdown[]
): { projection: ProjectionYearPoint[]; fireYear: number | null; warnings: string[]; purchaseGoalsMet: Record<string, boolean> } {
  const warnings: string[] = [];
  const projection: ProjectionYearPoint[] = [];
  
  const {
    currentAge,
    retirementAge,
    pensionEligibilityAge,
    lifeExpectancy,
    monthlySpend,
    monthlySavings,
    withdrawalRate,
    inflation,
    returnAssumptions,
    lifeEvents: rawLifeEvents,
    startPortfolioByCategory,
    includeNonLiquidRealestate,
    includeNonLiquidPension,
    expectedPensionAtRetirement,
    pensionCashflowAtRetirement,
    pensionLumpSumAtRetirement,
    totalMonthlyPensionContribution,
    monthlyIncome,
    pensionContributionRate,
  } = inputs;

  // Couple support: the second partner has their own age, retirement age, salary
  // and pension pool. The household is "retired" (switches to withdrawal mode)
  // only once BOTH partners have reached their retirement ages.
  const partner = inputs.partner;
  const hasPartner = partner != null;
  const partnerAgeOffset = hasPartner ? partner!.currentAge - currentAge : 0;
  // Primary age at which the partner reaches their retirement age.
  const partnerRetirementPrimaryAge = hasPartner ? partner!.retirementAge - partnerAgeOffset : retirementAge;
  const householdRetirementAge = hasPartner
    ? Math.max(retirementAge, partnerRetirementPrimaryAge)
    : retirementAge;
  const partnerProductIdSet = new Set((partner?.pensionProductIds ?? []).map((id) => String(id)));

  // When pensionContributionRate is set, derive contributions from salary dynamically.
  // This makes pension deposits responsive to changes in monthlyIncome.
  // Record the initial expected pension balance for scaling cashflow at eligibility.
  // Mutable because early kitzba draws reduce the pension that unlocks at eligibility.
  let initialExpectedPensionBalance = expectedPensionAtRetirement ?? 0;
  let expectedPensionForUnlock = expectedPensionAtRetirement ?? 0;

  // Early kitzba streams: annuities the user chose to start before the official
  // pension age when retiring early. They pay monthly income from their start age.
  const earlyKitzbaStreams: PensionEarlyKitzbaStream[] = inputs.pensionEarlyKitzbaStreams ?? [];
  const activeEarlyKitzba: PensionEarlyKitzbaStream[] = [];

  // PURCHASE_GOAL withdrawals are handled via auto-generated life events.
  // Track whether each purchase goal was met (portfolio could afford it at target age).
  const purchaseGoals = (inputs.goals ?? []).filter(
    g => g.enabled && g.kind === RetirementGoalKind.PURCHASE_GOAL && g.targetAge !== undefined && g.targetAmount !== undefined
  );
  const purchaseGoalsMet: Record<string, boolean> = {};

  // Normalize recurring events without endYear:
  // income-related → end at retirement, others → end at life expectancy
  // Milestone-mode events (AT_RETIREMENT, AT_PENSION, AT_LIFE_EXPECTANCY) are resolved here.
  const currentYearNow = new Date().getFullYear();
  const retirementYear = currentYearNow + (retirementAge - currentAge);
  const lifeExpectancyYear = currentYearNow + (lifeExpectancy - currentAge);
  const pensionYear = currentYearNow + ((pensionEligibilityAge ?? retirementAge) - currentAge);
  // Partner's retirement year (couple mode) so partner-owned income events can
  // default-end at the partner's own retirement rather than the primary's.
  const partnerRetirementYear = hasPartner
    ? currentYearNow + (partner!.retirementAge - partner!.currentAge)
    : undefined;
  const partnerPensionYear = hasPartner
    ? currentYearNow +
      ((partner!.pensionEligibilityAge ?? partner!.retirementAge) -
        partner!.currentAge)
    : undefined;
  const lifeEvents = normalizeLifeEventEndYears(
    rawLifeEvents,
    retirementYear,
    lifeExpectancyYear,
    pensionYear,
    partnerRetirementYear,
    partnerPensionYear,
  );

  // Decide whether to use product-level or category-level tracking
  const useProductLevelTracking = productBreakdowns && productBreakdowns.length > 0;

  // Initialize product balances if we have product breakdowns
  let productBalances: ProductBalance[] = [];
  let lockedPensionProducts: ProductBalance[] = [];
  // The partner's locked pension is tracked in a separate pool so it can unlock
  // at the partner's own eligibility age.
  let partnerInitialLocked = 0;

  if (useProductLevelTracking) {
    const initialized = initializeProductBalances(productBreakdowns, includeNonLiquidRealestate, includeNonLiquidPension);
    productBalances = initialized.spendableProducts;
    if (hasPartner && partnerProductIdSet.size > 0) {
      lockedPensionProducts = initialized.lockedPensionProducts.filter(
        (lp) => !partnerProductIdSet.has(String(lp.productId)),
      );
      partnerInitialLocked = initialized.lockedPensionProducts
        .filter((lp) => partnerProductIdSet.has(String(lp.productId)))
        .reduce((s, lp) => s + lp.balance, 0);
    } else {
      lockedPensionProducts = initialized.lockedPensionProducts;
    }
  } else if (hasPartner) {
    partnerInitialLocked = partner!.currentPensionValue ?? 0;
  }

  // Fall back to category-level for locked pension tracking
  const { spendable: categoryBalances, lockedPension: initialLockedPension } = 
    initializeCategoryBalances(startPortfolioByCategory, includeNonLiquidRealestate, includeNonLiquidPension);
  
  // Track locked pension balance for display purposes (primary partner only;
  // the partner's locked pension is tracked separately in partnerPool).
  let lockedPensionBalance = useProductLevelTracking 
    ? totalProductBalances(lockedPensionProducts)
    : Math.max(0, initialLockedPension - partnerInitialLocked);

  const partnerPool = createPartnerPensionPool(inputs, partnerInitialLocked);

  const annualSpend = monthlySpend * 12;
  let fireAchievedYear: number | null = null;
  const currentYear = new Date().getFullYear();
  const endYear = currentYear + (lifeExpectancy - currentAge);

  // Pension cashflow state: once pension is eligible, kitzba products provide
  // a recurring monthly income stream that grows with inflation.
  let pensionCashflowActive = false;
  let basePensionMonthlyIncome = pensionCashflowAtRetirement ?? 0;

  // Track what fraction of the original pension remains (reduced by early sells)
  let pensionFractionRemaining = 1.0;

  // Per-partner annual salaries (drive pension contributions). Household income
  // is the sum; income-adjustment events are scaled against these bases.
  const primaryAnnualIncome = (monthlyIncome ?? 0) * 12;
  const partnerAnnualIncome = (partner?.monthlyIncome ?? 0) * 12;

  for (let year = currentYear; year <= endYear; year++) {
    const age = currentAge + (year - currentYear);
    const yearsFromNow = year - currentYear;
    
    // Get current category balances (derived from products or direct)
    const currentCategoryBalances = useProductLevelTracking
      ? productBalancesToCategoryBalances(productBalances)
      : categoryBalances;
    
    // For year 0, record the starting portfolio before any changes
    if (yearsFromNow === 0) {
      const portfolio = useProductLevelTracking
        ? totalProductBalances(productBalances)
        : totalCategoryBalances(categoryBalances);
      const fireNumber = annualSpend / withdrawalRate;
      // Buying credit = CASH balance (they are the same pool)
      const cashBalance = currentCategoryBalances[AssetCategory.CASH] || 0;
      projection.push({
        year,
        age,
        portfolio,
        contributions: 0,
        withdrawals: 0,
        netCashflow: 0,
        eventsImpact: 0,
        incomeReduction: 0,
        investmentReturns: 0,
        fireAchieved: portfolio >= fireNumber,
        categoryBalances: { ...currentCategoryBalances },
        lockedPensionBalance: lockedPensionBalance > 0 ? lockedPensionBalance : undefined,
        buyingCredit: cashBalance > 0 ? cashBalance : undefined,
        productBalances: useProductLevelTracking
          ? productBalances.map(p => ({ ...p }))
          : undefined,
      });
      continue;
    }
    
    // Household retirement: both partners must have retired to switch to the
    // withdrawal phase. While one still works, we stay in the accumulation
    // branch (net income can be negative, drawing down the portfolio).
    const partnerAge = age + partnerAgeOffset;
    const primaryWorking = age < retirementAge;
    const partnerWorking = hasPartner && partnerAge < partner!.retirementAge;
    const isRetired = age >= householdRetirementAge;
    const isPensionEligibilityYear = age === pensionEligibilityAge;
    let pensionUnlockedThisYear = false;
    let soldAmount = 0;
    let boughtAmount = 0;
    // Partner's locked pension at end of year (for combined display).
    let partnerPensionLocked = 0;

    // Calculate inflation-adjusted amounts
    const inflationFactor = Math.pow(1 + inflation, yearsFromNow);
    const baseAnnualSpend = annualSpend * inflationFactor;

    // Calculate detailed life events impact
    const eventsResult = calculateDetailedLifeEventsImpact(lifeEvents, year);

    // Apply spending reduction from REDUCE_EXPENSES life events
    // These events lower spending rather than adding income
    const adjustedAnnualSpend = Math.max(0, baseAnnualSpend - eventsResult.spendingReduction);

    // Income-adjustment ratios (from increaseIncome / reduceIncome life events)
    // used to scale each partner's pension contributions. Contributions are a
    // percentage of salary, so lowering income lowers deposits and raising it
    // raises deposits — per partner.
    //
    // Two effects compose multiplicatively:
    //  - a household-level effect (events with no owner), applied to BOTH
    //    partners and scaled against total household income (legacy behavior);
    //  - a per-partner effect (owned events), scaled against that partner's own
    //    income and applied only to them.
    // A positive net adjustment means a net reduction; a negative one (net
    // increase) produces a scaling factor above 1.
    const householdAnnualIncome = monthlyIncome != null
      ? primaryAnnualIncome + partnerAnnualIncome
      : 0;
    const adj = eventsResult.incomeAdjustments;
    // Convert a (reduction - increase) net against a base income into a scaling
    // factor for contributions. factor < 1 => reduced, > 1 => increased.
    const toFactor = (net: number, baseIncome: number): number => {
      if (baseIncome <= 0) return 1;
      const ratio = Math.min(1, net / baseIncome); // cap reductions at 100%
      return 1 - ratio;
    };
    const householdFactor = toFactor(
      adj.household.reduction - adj.household.increase,
      householdAnnualIncome,
    );
    const primaryFactor = householdFactor * toFactor(
      adj.primary.reduction - adj.primary.increase,
      primaryAnnualIncome,
    );
    const partnerFactor = householdFactor * toFactor(
      adj.partner.reduction - adj.partner.increase,
      partnerAnnualIncome,
    );
    // Expressed as "reduction ratios" (1 - factor) for calculateReducedContribution.
    const primaryPensionReductionRatio = 1 - primaryFactor;
    const partnerPensionReductionRatio = 1 - partnerFactor;

    let contributions = 0;
    let withdrawals = 0;
    let netCashflow = 0;
    let yearPensionDeposit = 0;

    // Handle pension unlock at pension eligibility age.
    // Kitzba products start paying monthly cashflow; hon products become a lump sum.
    let yearPensionMonthlyIncome = 0;
    let yearPensionTaxAmount = 0;
    let yearPensionNetAnnualIncome = 0;

    // Activate early kitzba streams whose start age is reached this year.
    // Draw the kitzba portion out of the locked pool (no 35% tax, no cash
    // proceeds) so the remaining pension unlocks correctly at eligibility age.
    for (const stream of earlyKitzbaStreams) {
      if (age === stream.startAge && !activeEarlyKitzba.includes(stream)) {
        if (lockedPensionBalance > 0) {
          const reduction = Math.min(lockedPensionBalance, stream.projectedValueAtStart);
          const fraction = reduction / lockedPensionBalance;
          lockedPensionBalance -= reduction;
          initialExpectedPensionBalance *= (1 - fraction);
          expectedPensionForUnlock *= (1 - fraction);
          if (useProductLevelTracking && lockedPensionProducts.length > 0) {
            for (const lp of lockedPensionProducts) {
              lp.balance *= (1 - fraction);
            }
            lockedPensionBalance = totalProductBalances(lockedPensionProducts);
          }
        }
        activeEarlyKitzba.push(stream);
      }
    }

    if (isPensionEligibilityYear && (lockedPensionBalance > 0 || expectedPensionForUnlock > 0)) {
      // When contributions were salary-based, the actual accumulated balance may
      // differ from the static expected value. Scale cashflow and lump sum by
      // the ratio of actual/expected so the pension outcome reflects salary changes.
      const pensionScalingFactor = (pensionContributionRate != null && initialExpectedPensionBalance > 0)
        ? (lockedPensionBalance * pensionFractionRemaining) / initialExpectedPensionBalance
        : 1;

      // Lump-sum portion (hon products, net of tax) goes into the portfolio.
      // Reduced proportionally if some pension was sold early, and scaled by salary impact.
      const lumpSumToAdd = (pensionLumpSumAtRetirement ?? 0) * pensionFractionRemaining * pensionScalingFactor;

      // The remaining value (kitzba products) converts to monthly cashflow.
      // If we have per-product plans we use the calculated cashflow; otherwise
      // fall back to adding everything as a lump sum (legacy behavior).
      const hasCashflowPlan = basePensionMonthlyIncome > 0;

      if (hasCashflowPlan) {
        // Scale monthly pension income by the salary-driven accumulation difference
        basePensionMonthlyIncome *= pensionScalingFactor;

        // Only add the lump-sum portion to the portfolio
        if (lumpSumToAdd > 0) {
          if (useProductLevelTracking) {
            productBalances.push(createSyntheticProduct(
              "פנסיה - חלק הוני",
              AssetCategory.CASH,
              lumpSumToAdd,
            ));
          } else {
            categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + lumpSumToAdd;
          }
        }
        pensionCashflowActive = true;
      } else {
        // Legacy behavior: dump entire expected pension as lump sum
        const pensionToUnlock = expectedPensionForUnlock > 0 ? expectedPensionForUnlock : lockedPensionBalance;
        if (useProductLevelTracking) {
          productBalances.push(createSyntheticProduct(
            "פנסיה שהשתחררה",
            AssetCategory.PENSION,
            pensionToUnlock,
          ));
        } else {
          categoryBalances[AssetCategory.PENSION] += pensionToUnlock;
        }
      }

      if (useProductLevelTracking) lockedPensionProducts = [];
      lockedPensionBalance = 0;
      pensionUnlockedThisYear = true;
    }

    // Calculate pension kitzba income for this year (inflation-adjusted)
    if (pensionCashflowActive) {
      const inflatedMonthlyPension = basePensionMonthlyIncome * inflationFactor;
      const taxResult = calculateKitzbaTax(inflatedMonthlyPension, year, DEFAULT_MARGINAL_TAX_RATE);
      yearPensionMonthlyIncome = inflatedMonthlyPension;
      yearPensionTaxAmount = taxResult.taxAmount * 12;
      yearPensionNetAnnualIncome = taxResult.netPension * 12;
    }

    // Add early kitzba income (annuities started before eligibility age).
    // Taxed as a kitzba (kitzba mezaka exemption), never at the 35% early rate.
    for (const stream of activeEarlyKitzba) {
      const inflatedMonthly = stream.monthlyPension * inflationFactor;
      const taxResult = calculateKitzbaTax(inflatedMonthly, year, DEFAULT_MARGINAL_TAX_RATE);
      yearPensionMonthlyIncome += inflatedMonthly;
      yearPensionTaxAmount += taxResult.taxAmount * 12;
      yearPensionNetAnnualIncome += taxResult.netPension * 12;
    }

    // Advance the partner's pension pool (couple mode). Contributions accrue while
    // the partner works; the pool unlocks at the partner's own eligibility age,
    // adding a lump sum to the portfolio and a monthly kitzba to household income.
    if (partnerPool) {
      const partnerResult = partnerPool.step(partnerAge, year, inflationFactor, partnerPensionReductionRatio);
      if (partnerResult.monthlyIncome > 0) {
        yearPensionMonthlyIncome += partnerResult.monthlyIncome;
        yearPensionTaxAmount += partnerResult.monthlyIncome * 12 - partnerResult.netIncome;
        yearPensionNetAnnualIncome += partnerResult.netIncome;
      }
      if (partnerResult.lumpSum > 0) {
        if (useProductLevelTracking) {
          productBalances.push(createSyntheticProduct(
            "פנסיית בן/בת הזוג - חלק הוני",
            AssetCategory.CASH,
            partnerResult.lumpSum,
          ));
        } else {
          categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + partnerResult.lumpSum;
        }
        pensionUnlockedThisYear = true;
      }
      partnerPensionLocked = partnerPool.lockedBalance;
    }

    // 1. Process sell events
    //    Pension sell events pull from the LOCKED pension pool with 35% early withdrawal tax.
    //    Non-pension sell events proceed normally from the spendable pool.
    if (eventsResult.sellRequests.length > 0) {
      const pensionSells = eventsResult.sellRequests.filter(r => r.category === AssetCategory.PENSION);
      const normalSells = eventsResult.sellRequests.filter(r => r.category !== AssetCategory.PENSION);

      // Handle pension early withdrawal from locked pool (before pension eligibility)
      if (pensionSells.length > 0 && lockedPensionBalance > 0 && !pensionCashflowActive) {
        for (const req of pensionSells) {
          let sellAmt = 0;
          if (req.sellType === SellType.PERCENTAGE) {
            sellAmt = lockedPensionBalance * ((req.sellPercentage ?? req.percentage ?? 0) / 100);
          } else {
            sellAmt = Math.min(req.sellAmount ?? req.amount ?? 0, lockedPensionBalance);
          }

          if (sellAmt > 0) {
            const fractionSold = sellAmt / lockedPensionBalance;
            const tax = sellAmt * PENSION_TAX.EARLY_WITHDRAWAL_RATE;
            const netProceeds = sellAmt - tax;

            lockedPensionBalance -= sellAmt;
            pensionFractionRemaining *= (1 - fractionSold);
            basePensionMonthlyIncome = (pensionCashflowAtRetirement ?? 0) * pensionFractionRemaining;

            if (useProductLevelTracking && lockedPensionProducts.length > 0) {
              const scaleFactor = 1 - fractionSold;
              for (const lp of lockedPensionProducts) {
                lp.balance *= scaleFactor;
              }
            }

            if (useProductLevelTracking) {
              productBalances.push(createSyntheticProduct(
                "משיכה מוקדמת מפנסיה (נטו)",
                AssetCategory.CASH,
                netProceeds,
              ));
            } else {
              categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + netProceeds;
            }

            soldAmount += sellAmt;
          }
        }
      } else if (pensionSells.length > 0) {
        // After pension release, pension sells go through normal processing
        normalSells.push(...pensionSells);
      }

      // Process remaining (non-pension) sell events normally
      if (normalSells.length > 0) {
        if (useProductLevelTracking) {
          soldAmount += processSellRequestsFromProducts(normalSells, productBalances);
        } else {
          soldAmount += processSellRequests(normalSells, categoryBalances);
        }
      }
    }

    // 2. Apply cashflow impacts (inflows and outflows) directly to portfolio
    if (eventsResult.netImpact > 0) {
      // Positive cashflows (inflows) add to CASH
      if (useProductLevelTracking) {
        productBalances.push(createSyntheticProduct("הכנסה מאירוע", AssetCategory.CASH, eventsResult.netImpact));
      } else {
        categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + eventsResult.netImpact;
      }
    } else if (eventsResult.netImpact < 0) {
      // Negative cashflows (outflows) withdraw proportionally
      if (useProductLevelTracking) {
        withdrawFromProductsProportionally(-eventsResult.netImpact, productBalances);
      } else {
        withdrawProportionally(-eventsResult.netImpact, categoryBalances);
      }
    }

    if (!isRetired) {
      // Accumulation phase
      
      // 3. Net income vs expenses — add savings or withdraw deficit
      // When monthlyIncome is explicitly provided, use (income - expenses) which
      // can go negative to correctly withdraw the deficit from the portfolio.
      // When monthlyIncome is not set (legacy data/tests), fall back to the
      // UI-clamped monthlySavings (always >= 0).
      // Pension income (eligibility kitzba and/or early kitzba streams) boosts savings.
      const pensionIncomeBoost = yearPensionNetAnnualIncome;
      // Household income: each partner's salary is counted only while that partner
      // is still working (below their own retirement age).
      const householdMonthlyIncome =
        (primaryWorking ? (monthlyIncome ?? 0) : 0) +
        (partnerWorking ? (partner!.monthlyIncome ?? 0) : 0);
      const rawAnnualNet = monthlyIncome != null
        ? (householdMonthlyIncome - monthlySpend) * 12
        : monthlySavings * 12;
      const effectiveNet = rawAnnualNet + eventsResult.spendingReduction - eventsResult.incomeReduction + pensionIncomeBoost;
      if (effectiveNet > 0) {
        if (useProductLevelTracking) {
          productBalances.push(createSyntheticProduct("חיסכון", AssetCategory.CASH, effectiveNet));
        } else {
          categoryBalances[AssetCategory.CASH] = (categoryBalances[AssetCategory.CASH] || 0) + effectiveNet;
        }
        contributions = effectiveNet;
      } else if (effectiveNet < 0) {
        const deficit = -effectiveNet;
        if (useProductLevelTracking) {
          withdrawFromProductsProportionally(deficit, productBalances);
        } else {
          withdrawProportionally(deficit, categoryBalances);
        }
        withdrawals = deficit;
      }

      // 4. Process ALL buy requests from CASH/credit
      //    All buy events now use buying credit (= CASH pool)
      if (eventsResult.buyFromCreditRequests.length > 0) {
        // Available credit = current CASH balance
        const cashBalance = useProductLevelTracking
          ? productBalances.filter(p => p.category === AssetCategory.CASH && p.balance > 0).reduce((s, p) => s + p.balance, 0)
          : categoryBalances[AssetCategory.CASH] || 0;

        if (useProductLevelTracking) {
          const { spent } = processBuyFromCreditToProducts(
            eventsResult.buyFromCreditRequests,
            productBalances,
            cashBalance
          );
          boughtAmount = spent;
          contributions += spent;
        } else {
          const { spent } = processBuyFromCredit(
            eventsResult.buyFromCreditRequests,
            categoryBalances,
            cashBalance
          );
          boughtAmount = spent;
          contributions += spent;
        }
      }
      
      netCashflow = contributions - withdrawals + eventsResult.netImpact;
      
      // Compute the PRIMARY partner's pension contribution for this year.
      // When pensionContributionRate is set, derive from current salary so that
      // changing income in the UI dynamically affects pension deposits. The
      // partner's contributions are handled separately by partnerPool.
      // Contributions stop once the primary partner retires.
      let yearPensionContribution = 0;
      if (primaryWorking && pensionContributionRate != null && monthlyIncome != null) {
        const effectiveMonthlyIncome = calculateReducedContribution(monthlyIncome, primaryPensionReductionRatio);
        yearPensionContribution = effectiveMonthlyIncome * pensionContributionRate * 12;
      } else if (primaryWorking && totalMonthlyPensionContribution && totalMonthlyPensionContribution > 0) {
        yearPensionContribution = calculateReducedContribution(
          totalMonthlyPensionContribution,
          primaryPensionReductionRatio,
        ) * 12;
      }
      yearPensionDeposit = yearPensionContribution / 12;

      // Grow locked pension by its return rate and add contributions.
      if (useProductLevelTracking && lockedPensionProducts.length > 0) {
        if (yearPensionContribution > 0) {
          lockedPensionProducts.push({
            productId: `pension-contrib-${year}`,
            productName: "הפקדות פנסיה",
            category: AssetCategory.PENSION,
            balance: yearPensionContribution,
            isLiquid: false,
          });
        }
        applyReturnsPerProduct(lockedPensionProducts, returnAssumptions);
        lockedPensionBalance = totalProductBalances(lockedPensionProducts);
      } else if (lockedPensionBalance > 0) {
        lockedPensionBalance += yearPensionContribution;
        lockedPensionBalance *= (1 + returnAssumptions[AssetCategory.PENSION].expectedReturn);
      }
    } else {
      // Retirement phase
      // Pension kitzba income reduces the amount we need to withdraw from portfolio.
      const portfolioWithdrawalNeeded = Math.max(0, adjustedAnnualSpend - yearPensionNetAnnualIncome);
      withdrawals = portfolioWithdrawalNeeded;
      
      // Apply withdrawals proportionally
      if (useProductLevelTracking) {
        withdrawFromProductsProportionally(withdrawals, productBalances);
      } else {
        withdrawProportionally(withdrawals, categoryBalances);
      }
      
      // Process buy requests from CASH/credit
      if (eventsResult.buyFromCreditRequests.length > 0) {
        const cashBalance = useProductLevelTracking
          ? productBalances.filter(p => p.category === AssetCategory.CASH && p.balance > 0).reduce((s, p) => s + p.balance, 0)
          : categoryBalances[AssetCategory.CASH] || 0;

        if (useProductLevelTracking) {
          const { spent } = processBuyFromCreditToProducts(
            eventsResult.buyFromCreditRequests,
            productBalances,
            cashBalance
          );
          boughtAmount = spent;
        } else {
          const { spent } = processBuyFromCredit(
            eventsResult.buyFromCreditRequests,
            categoryBalances,
            cashBalance
          );
          boughtAmount = spent;
        }
      }
      
      netCashflow = -withdrawals + yearPensionNetAnnualIncome + eventsResult.netImpact;

      // Locked pension still grows from appreciation until eligibility (no new deposits after retirement).
      if (!pensionCashflowActive) {
        if (useProductLevelTracking && lockedPensionProducts.length > 0) {
          applyReturnsPerProduct(lockedPensionProducts, returnAssumptions);
          lockedPensionBalance = totalProductBalances(lockedPensionProducts);
        } else if (lockedPensionBalance > 0) {
          lockedPensionBalance *= (1 + returnAssumptions[AssetCategory.PENSION].expectedReturn);
        }
      }
    }

    // Track PURCHASE_GOAL affordability — the actual withdrawal is handled by the
    // auto-generated life event (one-time outflow). We just record whether the portfolio
    // BEFORE the withdrawal could have afforded the full purchase amount.
    for (const pGoal of purchaseGoals) {
      if (pGoal.targetAge === age && pGoal.targetAmount !== undefined) {
        const portfolioBeforeReturns = useProductLevelTracking
          ? totalProductBalances(productBalances)
          : totalCategoryBalances(categoryBalances);
        purchaseGoalsMet[pGoal.id] = portfolioBeforeReturns >= pGoal.targetAmount;
      }
    }

    // Apply investment returns
    let investmentReturns: number;
    if (useProductLevelTracking) {
      investmentReturns = applyReturnsPerProduct(productBalances, returnAssumptions);
    } else {
      investmentReturns = applyReturnsPerCategory(categoryBalances, returnAssumptions);
    }

    // Apply yearly cost on debt (negative CASH)
    if (useProductLevelTracking) {
      for (const p of productBalances) {
        if (p.category === AssetCategory.CASH && p.balance < 0) {
          p.balance *= 1 + DEBT_ANNUAL_COST_RATE;
        }
      }
    } else {
      if ((categoryBalances[AssetCategory.CASH] ?? 0) < 0) {
        categoryBalances[AssetCategory.CASH] *= 1 + DEBT_ANNUAL_COST_RATE;
      }
    }

    // Calculate total spendable portfolio
    const portfolio = useProductLevelTracking
      ? totalProductBalances(productBalances)
      : totalCategoryBalances(categoryBalances);
    
    // Get updated category balances for output
    const outputCategoryBalances = useProductLevelTracking
      ? productBalancesToCategoryBalances(productBalances)
      : { ...categoryBalances };

    // Check FIRE achievement
    const fireAchieved = portfolio * withdrawalRate >= adjustedAnnualSpend;
    if (fireAchieved && !fireAchievedYear && age < retirementAge) {
      fireAchievedYear = year;
    }

    // Legacy chart impact. Income increases are already reflected in the
    // per-partner salary series, so exclude them here to avoid displaying the
    // same income in both the salary and event-income bars.
    const incomeIncrease =
      (primaryAnnualIncome > 0
        ? eventsResult.incomeAdjustments.primary.increase
        : 0) +
      (partnerAnnualIncome > 0
        ? eventsResult.incomeAdjustments.partner.increase
        : 0) +
      (householdAnnualIncome > 0
        ? eventsResult.incomeAdjustments.household.increase
        : 0);
    const eventsImpact =
      eventsResult.netImpact - incomeIncrease + soldAmount + boughtAmount;

    // Buying credit = CASH balance (they are the same pool)
    const endOfYearCashBalance = outputCategoryBalances[AssetCategory.CASH] || 0;

    // Per-partner salary for the income/expenses chart. Values are in current
    // (real) terms after applying each partner's income-event adjustments, and
    // zero once that partner has retired. Left undefined when income is unknown
    // (legacy data) so the chart can fall back to its own reconstruction.
    const primarySalary = monthlyIncome != null
      ? (primaryWorking ? primaryAnnualIncome * primaryFactor : 0)
      : undefined;
    const partnerSalary = hasPartner
      ? (partnerWorking ? partnerAnnualIncome * partnerFactor : 0)
      : undefined;

    projection.push({
      year,
      age,
      portfolio,
      contributions,
      withdrawals,
      netCashflow,
      eventsImpact,
      incomeReduction: eventsResult.incomeReduction > 0 ? eventsResult.incomeReduction : undefined,
      primarySalary,
      partnerSalary,
      investmentReturns,
      fireAchieved,
      categoryBalances: outputCategoryBalances,
      lockedPensionBalance: (lockedPensionBalance + partnerPensionLocked) > 0
        ? lockedPensionBalance + partnerPensionLocked
        : undefined,
      pensionUnlockedThisYear,
      buyingCredit: endOfYearCashBalance > 0 ? endOfYearCashBalance : undefined,
      soldAmount: soldAmount > 0 ? soldAmount : undefined,
      boughtAmount: boughtAmount > 0 ? boughtAmount : undefined,
      productBalances: useProductLevelTracking
        ? productBalances.map(p => ({ ...p }))
        : undefined,
      pensionMonthlyIncome: yearPensionMonthlyIncome > 0 ? yearPensionMonthlyIncome : undefined,
      pensionTaxAmount: yearPensionTaxAmount > 0 ? yearPensionTaxAmount : undefined,
      pensionNetAnnualIncome: yearPensionNetAnnualIncome > 0 ? yearPensionNetAnnualIncome : undefined,
      pensionMonthlyDeposit: yearPensionDeposit > 0 ? yearPensionDeposit : undefined,
    });

    // Detect portfolio depletion or debt
    if (portfolio <= 0 && !warnings.some(w => w.includes("התיק נגמר"))) {
      warnings.push(`התיק נגמר בגיל ${age} (שנת ${year})`);
    }
    if (portfolio < 0 && !warnings.some(w => w.includes("חוב"))) {
      const debtFormatted = Math.round(Math.abs(portfolio)).toLocaleString("he-IL");
      warnings.push(`יש חוב של ${debtFormatted} ₪ בגיל ${age} (שנת ${year})`);
    }
  }

  // Add warning if monthly expenses imply a withdrawal rate exceeding the safe rate
  if (startingPortfolio > 0) {
    const impliedRate = annualSpend / startingPortfolio;
    if (impliedRate > withdrawalRate) {
      const impliedPercent = (impliedRate * 100).toFixed(1);
      const safePercent = (withdrawalRate * 100).toFixed(1);
      warnings.push(`ההוצאות החודשיות מרמזות על שיעור משיכה של ${impliedPercent}%, שחורג משיעור הבטוח של ${safePercent}%.`);
    }
  }

  return { projection, fireYear: fireAchievedYear, warnings, purchaseGoalsMet };
}

// ============================================================================
// MONTE CARLO HELPERS
// ============================================================================

/**
 * A Monte Carlo "bucket" tracks a chunk of money with its own yield/risk.
 * - Buckets without customReturn use the default category return assumption.
 * - Buckets with customReturn use the custom yield (and optionally a custom risk profile).
 * This allows purchased assets with a custom return to earn their own rate.
 */
interface MCBucket {
  category: AssetCategory;
  balance: number;
  customReturn?: { expectedReturn?: number; riskProfileId?: string; standardDeviation?: number };
}

/** Sum all bucket balances */
function mcTotalBuckets(buckets: MCBucket[]): number {
  return buckets.reduce((sum, b) => sum + b.balance, 0);
}

/**
 * Withdraw proportionally from buckets.
 * Prefers cash & deposit buckets first, then withdraws proportionally from the rest.
 * When balance is exhausted, remaining shortfall is recorded as debt (negative CASH bucket).
 */
function mcWithdrawFromBuckets(amount: number, buckets: MCBucket[]): void {
  let remaining = amount;

  // Prefer cash and deposit buckets first
  for (const bucket of buckets) {
    if (remaining <= 0) break;
    if ((bucket.category === AssetCategory.CASH || bucket.category === AssetCategory.DEPOSIT) && bucket.balance > 0) {
      const take = Math.min(remaining, bucket.balance);
      bucket.balance -= take;
      remaining -= take;
    }
  }

  if (remaining <= 0) return;

  // Withdraw proportionally from all remaining buckets
  const total = buckets.reduce((sum, b) => sum + Math.max(0, b.balance), 0);
  if (total <= 0) {
    // No positive balance — shortfall becomes debt (negative CASH bucket)
    const debtBucket = buckets.find((b) => b.category === AssetCategory.CASH && b.balance < 0);
    if (debtBucket) {
      debtBucket.balance -= remaining;
    } else {
      buckets.push({ category: AssetCategory.CASH, balance: -remaining });
    }
    return;
  }

  const ratio = Math.min(1, remaining / total);
  const withdrawn = total * ratio;
  for (const bucket of buckets) {
    if (bucket.balance > 0) {
      bucket.balance -= bucket.balance * ratio;
    }
  }
  remaining -= withdrawn;

  // Any remaining shortfall becomes debt
  if (remaining > 0) {
    const debtBucket = buckets.find((b) => b.category === AssetCategory.CASH && b.balance < 0);
    if (debtBucket) {
      debtBucket.balance -= remaining;
    } else {
      buckets.push({ category: AssetCategory.CASH, balance: -remaining });
    }
  }
}

/**
 * Add an amount to buckets.
 * - If it has a custom return assumption, create a new separate bucket.
 * - Otherwise, merge into an existing bucket for the same category (no custom return).
 */
function mcAddToBuckets(
  buckets: MCBucket[],
  category: AssetCategory,
  amount: number,
  customReturnAssumption?: { expectedReturn?: number; riskProfileId?: string; standardDeviation?: number }
): void {
  const hasCustom = customReturnAssumption &&
    (customReturnAssumption.expectedReturn !== undefined ||
     customReturnAssumption.riskProfileId !== undefined);

  if (hasCustom) {
    // Custom yield — track separately so it earns its own returns
    buckets.push({ category, balance: amount, customReturn: customReturnAssumption });
  } else {
    // Default yield — merge into existing default bucket for this category
    const existing = buckets.find(b => b.category === category && !b.customReturn);
    if (existing) {
      existing.balance += amount;
    } else {
      buckets.push({ category, balance: amount });
    }
  }
}

// ============================================================================
// GOAL EVALUATION HELPERS
// ============================================================================

/**
 * Evaluate whether a single goal is met for a given simulation's yearly portfolio values.
 *
 * @param goal - The goal to evaluate
 * @param portfolioByAge - Map from age → portfolio value for this simulation
 * @param currentAge - The user's current age
 * @param lifeExpectancy - Life expectancy age
 * @param fireNumber - FIRE number (annualSpend / withdrawalRate)
 * @returns true if the goal is met in this simulation
 */
function evaluateGoalForSimulation(
  goal: RetirementGoal,
  portfolioByAge: Map<number, number>,
  currentAge: number,
  lifeExpectancy: number,
  fireNumber: number
): boolean {
  if (!goal.enabled) return true; // Disabled goals always pass

  switch (goal.kind) {
    case RetirementGoalKind.PORTFOLIO_TARGET: {
      // Have at least targetAmount at targetAge
      if (goal.targetAmount === undefined || goal.targetAge === undefined) return true;
      const value = portfolioByAge.get(goal.targetAge);
      return value !== undefined && value >= goal.targetAmount;
    }

    case RetirementGoalKind.PORTFOLIO_SURVIVES: {
      // Portfolio stays > 0 through targetAge (default: lifeExpectancy)
      const targetAge = goal.targetAge ?? lifeExpectancy;
      for (let age = currentAge; age <= targetAge; age++) {
        const value = portfolioByAge.get(age);
        if (value !== undefined && value <= 0) return false;
      }
      return true;
    }

    case RetirementGoalKind.FIRE_BY_AGE: {
      // Reach FIRE number by targetAge
      if (goal.targetAge === undefined) return true;
      for (let age = currentAge; age <= goal.targetAge; age++) {
        const value = portfolioByAge.get(age);
        if (value !== undefined && value >= fireNumber) return true;
      }
      return false;
    }

    case RetirementGoalKind.MINIMUM_FLOOR: {
      // Portfolio never drops below targetAmount
      if (goal.targetAmount === undefined) return true;
      for (const [, value] of portfolioByAge) {
        if (value < goal.targetAmount) return false;
      }
      return true;
    }

    case RetirementGoalKind.PURCHASE_GOAL:
      // PURCHASE_GOAL is handled inline during the simulation loop.
      // If this function is called for a PURCHASE_GOAL, it means
      // the inline logic already applied the withdrawal and the result
      // is tracked via purchaseGoalMet. Return true here as a fallback.
      return true;

    default:
      return true;
  }
}

/**
 * Evaluate goals against a deterministic projection.
 * Returns a GoalResult[] with deterministicMet and metAtAge for each goal.
 */
function evaluateGoalsDeterministic(
  goals: RetirementGoal[],
  projection: ProjectionYearPoint[],
  fireNumber: number,
  lifeExpectancy: number,
  purchaseGoalsMet: Record<string, boolean> = {}
): GoalResult[] {
  const enabledGoals = goals.filter(g => g.enabled);
  if (enabledGoals.length === 0) return [];

  return enabledGoals.map((goal) => {
    let deterministicMet = false;
    let metAtAge: number | undefined;

    switch (goal.kind) {
      case RetirementGoalKind.PORTFOLIO_TARGET: {
        if (goal.targetAmount === undefined || goal.targetAge === undefined) {
          deterministicMet = true;
          break;
        }
        const point = projection.find(p => p.age === goal.targetAge);
        deterministicMet = point !== undefined && point.portfolio >= goal.targetAmount;
        if (deterministicMet && point) metAtAge = point.age;
        // Also check if we reach it earlier
        for (const p of projection) {
          if (p.portfolio >= goal.targetAmount!) {
            metAtAge = p.age;
            break;
          }
        }
        break;
      }

      case RetirementGoalKind.PORTFOLIO_SURVIVES: {
        const targetAge = goal.targetAge ?? lifeExpectancy;
        deterministicMet = true;
        for (const p of projection) {
          if (p.age <= targetAge && p.portfolio <= 0) {
            deterministicMet = false;
            break;
          }
        }
        break;
      }

      case RetirementGoalKind.FIRE_BY_AGE: {
        if (goal.targetAge === undefined) {
          deterministicMet = true;
          break;
        }
        for (const p of projection) {
          if (p.age <= goal.targetAge && p.portfolio >= fireNumber) {
            deterministicMet = true;
            metAtAge = p.age;
            break;
          }
        }
        break;
      }

      case RetirementGoalKind.MINIMUM_FLOOR: {
        if (goal.targetAmount === undefined) {
          deterministicMet = true;
          break;
        }
        deterministicMet = true;
        for (const p of projection) {
          if (p.portfolio < goal.targetAmount) {
            deterministicMet = false;
            break;
          }
        }
        break;
      }

      case RetirementGoalKind.PURCHASE_GOAL: {
        // PURCHASE_GOAL was already applied during the deterministic projection.
        // The purchaseGoalsMet map tells us if the portfolio could afford it.
        deterministicMet = purchaseGoalsMet[goal.id] ?? false;
        if (deterministicMet && goal.targetAge !== undefined) {
          metAtAge = goal.targetAge;
        }
        break;
      }
    }

    return {
      goalId: goal.id,
      successProbability: 0, // Will be set by Monte Carlo; 0 is placeholder for deterministic-only
      deterministicMet,
      metAtAge,
    };
  });
}

// ============================================================================
// MONTE CARLO SIMULATION
// ============================================================================

/**
 * Run Monte Carlo simulation
 * Now handles all life event types: cashflow, buy asset, and sell asset events.
 * Tracks buying credit (from savings, sell proceeds, inflows) for buy-from-credit events.
 *
 * Uses bucket-based tracking so that:
 * - Initial assets earn their category's default return/risk.
 * - Purchased assets with a custom return assumption earn their own custom rate.
 * - Purchased assets without a custom return merge into the category default bucket.
 * Each year's purchases update the portfolio and subsequent returns
 * reflect the evolving allocation and per-bucket yields.
 */
export function runMonteCarloSimulation(
  inputs: AdvancedRetirementInputs,
  startingPortfolio: number,
  numSimulations: number = 500,
  productBreakdowns?: ProductBreakdown[],
  customRiskProfiles: RiskProfile[] = []
): MonteCarloResult {
  const {
    currentAge,
    retirementAge,
    pensionEligibilityAge,
    lifeExpectancy,
    monthlySpend,
    monthlySavings,
    monthlyIncome,
    inflation,
    returnAssumptions,
    lifeEvents: rawLifeEvents,
    startPortfolioByCategory,
    expectedPensionAtRetirement,
    includeNonLiquidRealestate,
    pensionCashflowAtRetirement,
    pensionLumpSumAtRetirement,
    totalMonthlyPensionContribution,
    pensionContributionRate,
  } = inputs;

  // Couple support (see runDeterministicProjection for the full rationale).
  const partner = inputs.partner;
  const hasPartner = partner != null;
  const partnerAgeOffset = hasPartner ? partner!.currentAge - currentAge : 0;
  const householdRetirementAge = hasPartner
    ? Math.max(retirementAge, partner!.retirementAge - partnerAgeOffset)
    : retirementAge;

  const currentYear = new Date().getFullYear();
  const endYear = currentYear + (lifeExpectancy - currentAge);

  const mcRetirementYear = currentYear + (retirementAge - currentAge);
  const mcPensionYear = currentYear + ((pensionEligibilityAge ?? retirementAge) - currentAge);
  const mcPartnerRetirementYear = hasPartner
    ? currentYear + (partner!.retirementAge - partner!.currentAge)
    : undefined;
  const mcPartnerPensionYear = hasPartner
    ? currentYear +
      ((partner!.pensionEligibilityAge ?? partner!.retirementAge) -
        partner!.currentAge)
    : undefined;
  const lifeEvents = normalizeLifeEventEndYears(
    rawLifeEvents,
    mcRetirementYear,
    endYear,
    mcPensionYear,
    mcPartnerRetirementYear,
    mcPartnerPensionYear,
  );
  const numYears = endYear - currentYear + 1;
  const annualSpend = monthlySpend * 12;
  const mcPrimaryAnnualIncome = (monthlyIncome ?? 0) * 12;
  const mcPartnerAnnualIncome = (partner?.monthlyIncome ?? 0) * 12;

  const mcBasePensionMonthly = pensionCashflowAtRetirement ?? 0;
  const mcHasCashflowPlan = mcBasePensionMonthly > 0;

  // Early kitzba streams (annuities started before the official pension age).
  const mcEarlyKitzbaStreams: PensionEarlyKitzbaStream[] = inputs.pensionEarlyKitzbaStreams ?? [];

  // Build the initial set of buckets (one per category with a positive balance)
  // Pension is locked initially and will be released at eligibility age via expectedPensionAtRetirement
  // Real estate is included only if includeNonLiquidRealestate is true
  const allCategories = Object.values(AssetCategory);
  const initialBuckets: MCBucket[] = [];
  for (const cat of allCategories) {
    let value = 0;
    if (cat === AssetCategory.PENSION) {
      value = 0; // Pension starts locked
    } else if (cat === AssetCategory.REALESTATE) {
      value = includeNonLiquidRealestate ? (startPortfolioByCategory[cat] || 0) : 0;
    } else {
      value = startPortfolioByCategory[cat] || 0;
    }
    if (value > 0) {
      initialBuckets.push({ category: cat, balance: value });
    }
  }

  // Store all simulation results by year
  const allResults: number[][] = Array.from({ length: numYears }, () => []);
  let successCount = 0;

  // Goal tracking
  const enabledGoals = (inputs.goals ?? []).filter(g => g.enabled);
  const hasGoals = enabledGoals.length > 0;
  const goalSuccessCounts: Record<string, number> = {};
  let allGoalsMetCount = 0;
  for (const g of enabledGoals) {
    goalSuccessCounts[g.id] = 0;
  }

  // Collect PURCHASE_GOAL goals — these actively withdraw from the portfolio during simulation
  const purchaseGoals = enabledGoals.filter(
    g => g.kind === RetirementGoalKind.PURCHASE_GOAL && g.targetAge !== undefined && g.targetAmount !== undefined
  );

  // FIRE number for goal evaluation
  const mcFireNumber = annualSpend / (inputs.withdrawalRate || 0.04);

  // Initial locked pension value for MC (mirrors deterministic locked pool).
  // The partner's share is tracked separately in their own pension pool.
  const mcPartnerInitialLocked = hasPartner ? (partner!.currentPensionValue ?? 0) : 0;
  const mcInitialLockedPension = Math.max(0, (startPortfolioByCategory[AssetCategory.PENSION] || 0) - mcPartnerInitialLocked);
  const mcInitialExpectedPensionBalance = expectedPensionAtRetirement ?? 0;

  // Run simulations
  for (let sim = 0; sim < numSimulations; sim++) {
    // Clone initial buckets for this simulation
    const buckets: MCBucket[] = initialBuckets.map(b => ({ ...b }));
    let success = true;
    let pensionReleased = false;
    let mcLockedPension = mcInitialLockedPension;
    let mcPensionFraction = 1.0;
    let mcSimPensionMonthly = mcBasePensionMonthly;
    // Per-sim early kitzba state (mutable expected balances so early draws reduce
    // the pension that unlocks at eligibility).
    let mcSimInitialExpected = mcInitialExpectedPensionBalance;
    let mcSimExpectedForUnlock = expectedPensionAtRetirement ?? 0;
    const mcActiveStreams: PensionEarlyKitzbaStream[] = [];
    // Partner pension pool for this simulation (couple mode). Pension grows
    // deterministically, so this yields the same result across simulations.
    const mcPartnerPool = createPartnerPensionPool(inputs, mcPartnerInitialLocked);

    // Track portfolio by age for goal evaluation
    const simPortfolioByAge = hasGoals ? new Map<number, number>() : null;

    // Track PURCHASE_GOAL outcomes for this simulation
    const purchaseGoalMet: Record<string, boolean> = {};

    for (let yearIdx = 0; yearIdx < numYears; yearIdx++) {
      const year = currentYear + yearIdx;
      const age = currentAge + yearIdx;
      const partnerAge = age + partnerAgeOffset;
      const primaryWorking = age < retirementAge;
      const partnerWorking = hasPartner && partnerAge < partner!.retirementAge;
      const isRetired = age >= householdRetirementAge;
      const isPensionEligibilityYear = age === pensionEligibilityAge;

      // Calculate total portfolio from all buckets
      const portfolioTotal = mcTotalBuckets(buckets);

      // For year 0, record the starting portfolio before any changes
      if (yearIdx === 0) {
        allResults[yearIdx].push(portfolioTotal);
        if (simPortfolioByAge) simPortfolioByAge.set(age, portfolioTotal);
        continue;
      }

      // Calculate inflation-adjusted amounts
      const inflationFactor = Math.pow(1 + inflation, yearIdx);
      const baseAnnualSpend = annualSpend * inflationFactor;

      // Calculate detailed life events impact (includes buy/sell asset events)
      const eventsResult = calculateDetailedLifeEventsImpact(lifeEvents, year);

      // Apply spending reduction from REDUCE_EXPENSES life events
      const adjustedAnnualSpend = Math.max(0, baseAnnualSpend - eventsResult.spendingReduction);

      // Keep partner-specific pension contribution adjustments consistent with
      // the deterministic projection.
      const mcToIncomeFactor = (net: number, baseIncome: number): number => {
        if (baseIncome <= 0) return 1;
        return 1 - Math.min(1, net / baseIncome);
      };
      const mcAdjustments = eventsResult.incomeAdjustments;
      const mcHouseholdFactor = mcToIncomeFactor(
        mcAdjustments.household.reduction - mcAdjustments.household.increase,
        mcPrimaryAnnualIncome + mcPartnerAnnualIncome,
      );
      const mcPrimaryFactor = mcHouseholdFactor * mcToIncomeFactor(
        mcAdjustments.primary.reduction - mcAdjustments.primary.increase,
        mcPrimaryAnnualIncome,
      );
      const mcPartnerFactor = mcHouseholdFactor * mcToIncomeFactor(
        mcAdjustments.partner.reduction - mcAdjustments.partner.increase,
        mcPartnerAnnualIncome,
      );
      const mcPrimaryPensionReductionRatio = 1 - mcPrimaryFactor;
      const mcPartnerPensionReductionRatio = 1 - mcPartnerFactor;

      // Activate early kitzba streams reaching their start age this year.
      // Draw the kitzba portion out of locked pension (no tax, no cash proceeds).
      for (const stream of mcEarlyKitzbaStreams) {
        if (age === stream.startAge && !mcActiveStreams.includes(stream)) {
          if (mcLockedPension > 0) {
            const reduction = Math.min(mcLockedPension, stream.projectedValueAtStart);
            const fraction = reduction / mcLockedPension;
            mcLockedPension -= reduction;
            mcSimInitialExpected *= (1 - fraction);
            mcSimExpectedForUnlock *= (1 - fraction);
          }
          mcActiveStreams.push(stream);
        }
      }

      // Net annual early kitzba income (taxed as kitzba) for this year.
      // Also folds in the partner's pension kitzba income (couple mode) so it
      // reaches both the accumulation and retirement branches below.
      let mcEarlyKitzbaNetIncome = 0;
      for (const stream of mcActiveStreams) {
        const inflated = stream.monthlyPension * inflationFactor;
        const taxResult = calculateKitzbaTax(inflated, year, DEFAULT_MARGINAL_TAX_RATE);
        mcEarlyKitzbaNetIncome += taxResult.netPension * 12;
      }

      // Advance the partner's pension pool (couple mode).
      if (mcPartnerPool) {
        const partnerResult = mcPartnerPool.step(
          partnerAge,
          year,
          inflationFactor,
          mcPartnerPensionReductionRatio,
        );
        mcEarlyKitzbaNetIncome += partnerResult.netIncome;
        if (partnerResult.lumpSum > 0) {
          mcAddToBuckets(buckets, AssetCategory.CASH, partnerResult.lumpSum);
        }
      }

      // Handle pension release at pension eligibility age
      if (isPensionEligibilityYear && !pensionReleased) {
        // Scale pension cashflow and lump sum by actual accumulated balance
        const mcPensionScale = (pensionContributionRate != null && mcSimInitialExpected > 0)
          ? (mcLockedPension * mcPensionFraction) / mcSimInitialExpected
          : 1;

        if (mcHasCashflowPlan) {
          mcSimPensionMonthly = mcBasePensionMonthly * mcPensionFraction * mcPensionScale;
          const adjustedLumpSum = (pensionLumpSumAtRetirement ?? 0) * mcPensionFraction * mcPensionScale;
          if (adjustedLumpSum > 0) {
            mcAddToBuckets(buckets, AssetCategory.CASH, adjustedLumpSum);
          }
        } else if (mcSimExpectedForUnlock > 0) {
          const adjustedExpected = mcSimExpectedForUnlock * mcPensionFraction;
          mcAddToBuckets(buckets, AssetCategory.PENSION, adjustedExpected);
        }
        pensionReleased = true;
        mcLockedPension = 0;
      }

      // 1. Process sell events
      //    Pension sell events pull from locked pension with 35% early withdrawal tax.
      if (eventsResult.sellRequests.length > 0) {
        const pensionSells = eventsResult.sellRequests.filter(r => r.category === AssetCategory.PENSION);
        const mcNormalSells = eventsResult.sellRequests.filter(r => r.category !== AssetCategory.PENSION);

        if (pensionSells.length > 0 && mcLockedPension > 0 && !pensionReleased) {
          for (const req of pensionSells) {
            let sellAmt = 0;
            if (req.sellType === SellType.PERCENTAGE) {
              sellAmt = mcLockedPension * ((req.sellPercentage ?? req.percentage ?? 0) / 100);
            } else {
              sellAmt = Math.min(req.sellAmount ?? req.amount ?? 0, mcLockedPension);
            }
            if (sellAmt > 0) {
              const fractionSold = sellAmt / mcLockedPension;
              const netProceeds = sellAmt * (1 - PENSION_TAX.EARLY_WITHDRAWAL_RATE);
              mcLockedPension -= sellAmt;
              mcPensionFraction *= (1 - fractionSold);
              mcSimPensionMonthly = mcBasePensionMonthly * mcPensionFraction;
              mcAddToBuckets(buckets, AssetCategory.CASH, netProceeds);
            }
          }
        } else if (pensionSells.length > 0) {
          mcNormalSells.push(...pensionSells);
        }

        for (const req of mcNormalSells) {
          const catBuckets = buckets.filter(b => b.category === req.category && b.balance > 0);
          const catTotal = catBuckets.reduce((s, b) => s + b.balance, 0);
          let sellAmt = 0;
          if (req.sellType === SellType.PERCENTAGE) {
            sellAmt = catTotal * ((req.sellPercentage ?? req.percentage ?? 0) / 100);
          } else {
            sellAmt = Math.min(req.sellAmount ?? req.amount ?? 0, catTotal);
          }
          if (sellAmt > 0) {
            let remaining = sellAmt;
            for (const bucket of catBuckets) {
              if (remaining <= 0) break;
              const take = Math.min(remaining, bucket.balance);
              bucket.balance -= take;
              remaining -= take;
            }
            mcAddToBuckets(buckets, AssetCategory.CASH, sellAmt);
          }
        }
      }

      // 2. Apply cashflow impacts (inflows and outflows)
      if (eventsResult.netImpact > 0) {
        mcAddToBuckets(buckets, AssetCategory.CASH, eventsResult.netImpact);
      } else if (eventsResult.netImpact < 0) {
        mcWithdrawFromBuckets(-eventsResult.netImpact, buckets);
      }

      if (!isRetired) {
        // Accumulation phase

        // 3. Net income vs expenses — add savings or withdraw deficit
        // Use raw income - expenses so deficit is properly withdrawn from portfolio.
        let mcPensionIncomeBoost = mcEarlyKitzbaNetIncome;
        if (pensionReleased && mcHasCashflowPlan) {
          const inflatedPension = mcSimPensionMonthly * inflationFactor;
          const taxResult = calculateKitzbaTax(inflatedPension, year, DEFAULT_MARGINAL_TAX_RATE);
          mcPensionIncomeBoost += taxResult.netPension * 12;
        }
        // Household income: each partner's salary counts only while working.
        const mcHouseholdMonthlyIncome =
          (primaryWorking ? (monthlyIncome ?? 0) : 0) +
          (partnerWorking ? (partner!.monthlyIncome ?? 0) : 0);
        const rawAnnualNet = monthlyIncome != null
          ? (mcHouseholdMonthlyIncome - monthlySpend) * 12
          : monthlySavings * 12;
        const effectiveNet = rawAnnualNet + eventsResult.spendingReduction - eventsResult.incomeReduction + mcPensionIncomeBoost;
        if (effectiveNet > 0) {
          mcAddToBuckets(buckets, AssetCategory.CASH, effectiveNet);
        } else if (effectiveNet < 0) {
          mcWithdrawFromBuckets(-effectiveNet, buckets);
        }

        // 4. Process ALL buy requests from CASH/credit
        //    Available credit = current CASH bucket total
        //    Track running cashAvailable to avoid recomputing for each request
        let cashAvailable = buckets
          .filter(b => b.category === AssetCategory.CASH && b.balance > 0)
          .reduce((sum, b) => sum + b.balance, 0);

        for (const buyRequest of eventsResult.buyFromCreditRequests) {
          const spent = Math.min(buyRequest.amount, cashAvailable);
          if (spent > 0) {
            mcAddToBuckets(buckets, buyRequest.category, spent, buyRequest.customReturnAssumption);
            // Withdraw from CASH buckets
            let remaining = spent;
            for (const bucket of buckets) {
              if (remaining <= 0) break;
              if (bucket.category === AssetCategory.CASH && bucket.balance > 0) {
                const take = Math.min(remaining, bucket.balance);
                bucket.balance -= take;
                remaining -= take;
              }
            }
            // Update running cashAvailable
            cashAvailable -= spent;
          }
        }

        // Grow locked pension and add salary-based contributions (primary partner).
        // Contributions stop once the primary partner retires.
        if (mcLockedPension > 0 || pensionContributionRate != null || totalMonthlyPensionContribution) {
          let mcPensionContrib = 0;
          if (primaryWorking && pensionContributionRate != null && monthlyIncome != null) {
            const mcEffectiveIncome = calculateReducedContribution(
              monthlyIncome,
              mcPrimaryPensionReductionRatio,
            );
            mcPensionContrib = mcEffectiveIncome * pensionContributionRate * 12;
          } else if (primaryWorking && totalMonthlyPensionContribution && totalMonthlyPensionContribution > 0) {
            mcPensionContrib = calculateReducedContribution(
              totalMonthlyPensionContribution,
              mcPrimaryPensionReductionRatio,
            ) * 12;
          }
          mcLockedPension += mcPensionContrib;
          mcLockedPension *= (1 + returnAssumptions[AssetCategory.PENSION].expectedReturn);
        }
      } else {
        // Retirement phase - pension kitzba reduces required portfolio withdrawals
        let mcPensionNetIncome = mcEarlyKitzbaNetIncome;
        if (pensionReleased && mcHasCashflowPlan) {
          const inflatedPension = mcSimPensionMonthly * inflationFactor;
          const taxResult = calculateKitzbaTax(inflatedPension, year, DEFAULT_MARGINAL_TAX_RATE);
          mcPensionNetIncome += taxResult.netPension * 12;
        }
        const mcPortfolioWithdrawal = Math.max(0, adjustedAnnualSpend - mcPensionNetIncome);
        mcWithdrawFromBuckets(mcPortfolioWithdrawal, buckets);

        // Process buy requests from CASH/credit
        //    Track running cashAvailable to avoid recomputing for each request
        let cashAvailable = buckets
          .filter(b => b.category === AssetCategory.CASH && b.balance > 0)
          .reduce((sum, b) => sum + b.balance, 0);

        for (const buyRequest of eventsResult.buyFromCreditRequests) {
          const spent = Math.min(buyRequest.amount, cashAvailable);
          if (spent > 0) {
            mcAddToBuckets(buckets, buyRequest.category, spent, buyRequest.customReturnAssumption);
            // Withdraw from CASH buckets
            let remaining = spent;
            for (const bucket of buckets) {
              if (remaining <= 0) break;
              if (bucket.category === AssetCategory.CASH && bucket.balance > 0) {
                const take = Math.min(remaining, bucket.balance);
                bucket.balance -= take;
                remaining -= take;
              }
            }
            // Update running cashAvailable
            cashAvailable -= spent;
          }
        }

        // Locked pension still grows from appreciation until eligibility (no new deposits after retirement).
        if (!pensionReleased && mcLockedPension > 0) {
          mcLockedPension *= (1 + returnAssumptions[AssetCategory.PENSION].expectedReturn);
        }
      }

      // Track PURCHASE_GOAL affordability — the actual withdrawal is handled by the
      // auto-generated life event processed earlier in eventsResult.
      // We record whether the portfolio could have afforded the full purchase.
      for (const pGoal of purchaseGoals) {
        if (pGoal.targetAge === age && pGoal.targetAmount !== undefined) {
          const portfolioBeforeReturns = mcTotalBuckets(buckets);
          purchaseGoalMet[pGoal.id] = portfolioBeforeReturns >= pGoal.targetAmount;
        }
      }

      // Apply random investment returns independently per bucket
      // Each bucket gets its own sampled return:
      //   - Custom-return buckets use their custom expectedReturn and risk profile
      //   - Default buckets use the category's return assumption
      for (const bucket of buckets) {
        if (bucket.balance <= 0) continue;

        // Determine effective expected return and risk profile for this bucket
        let expectedReturn: number;
        let profileId: string | undefined;

        if (bucket.customReturn?.expectedReturn !== undefined) {
          expectedReturn = bucket.customReturn.expectedReturn;
          // Use custom risk profile if specified, otherwise fall back to category default
          profileId = bucket.customReturn.riskProfileId
            ?? returnAssumptions[bucket.category]?.riskProfileId
            ?? DEFAULT_PROFILE_BY_CATEGORY[bucket.category];
        } else {
          const assumption = returnAssumptions[bucket.category];
          if (!assumption) continue;
          expectedReturn = assumption.expectedReturn;
          profileId = assumption.riskProfileId ?? DEFAULT_PROFILE_BY_CATEGORY[bucket.category];
        }

        const profile = profileId ? findProfileById(profileId, customRiskProfiles) : undefined;
        const r = profile
          ? sampleAnnualReturn(expectedReturn, profile)
          : expectedReturn;

        bucket.balance *= (1 + r);
      }

      // Apply yearly cost on debt (negative CASH buckets)
      for (const bucket of buckets) {
        if (bucket.category === AssetCategory.CASH && bucket.balance < 0) {
          bucket.balance *= 1 + DEBT_ANNUAL_COST_RATE;
        }
      }

      // Record total portfolio (can be negative when in debt)
      const endPortfolio = mcTotalBuckets(buckets);
      allResults[yearIdx].push(endPortfolio);
      if (simPortfolioByAge) simPortfolioByAge.set(age, endPortfolio);

      if (endPortfolio <= 0) success = false;
    }

    if (success) successCount++;

    // Evaluate goals for this simulation
    if (hasGoals && simPortfolioByAge) {
      let allGoalsMet = true;
      for (const goal of enabledGoals) {
        let met: boolean;
        if (goal.kind === RetirementGoalKind.PURCHASE_GOAL) {
          // PURCHASE_GOAL was handled inline during the simulation loop
          met = purchaseGoalMet[goal.id] ?? false;
        } else {
          met = evaluateGoalForSimulation(
            goal,
            simPortfolioByAge,
            currentAge,
            lifeExpectancy,
            mcFireNumber
          );
        }
        if (met) {
          goalSuccessCounts[goal.id]++;
        } else {
          allGoalsMet = false;
        }
      }
      if (allGoalsMet) allGoalsMetCount++;
    }
  }

  // Calculate percentiles
  const percentiles = allResults.map((yearResults, idx) => {
    const sorted = [...yearResults].sort((a, b) => a - b);
    const year = currentYear + idx;
    const age = currentAge + idx;

    return {
      year,
      age,
      p5: sorted[Math.floor(sorted.length * 0.05)] || 0,
      p10: sorted[Math.floor(sorted.length * 0.1)] || 0,
      p20: sorted[Math.floor(sorted.length * 0.2)] || 0,
      p25: sorted[Math.floor(sorted.length * 0.25)] || 0,
      p50: sorted[Math.floor(sorted.length * 0.5)] || 0,
      p75: sorted[Math.floor(sorted.length * 0.75)] || 0,
      p80: sorted[Math.floor(sorted.length * 0.8)] || 0,
      p90: sorted[Math.floor(sorted.length * 0.9)] || 0,
      p95: sorted[Math.floor(sorted.length * 0.95)] || 0,
    };
  });

  // Build goal results
  const goalResults: GoalResult[] | undefined = hasGoals
    ? enabledGoals.map((goal) => ({
        goalId: goal.id,
        successProbability: goalSuccessCounts[goal.id] / numSimulations,
        deterministicMet: false, // Will be filled in by runProjection
      }))
    : undefined;

  return {
    successProbability: successCount / numSimulations,
    percentiles,
    simulationCount: numSimulations,
    goalResults,
    overallGoalSuccessProbability: hasGoals ? allGoalsMetCount / numSimulations : undefined,
  };
}

// ============================================================================
// MAIN PROJECTION FUNCTION
// ============================================================================

/**
 * Main projection function that combines deterministic and Monte Carlo
 */
export function runProjection(
  inputs: AdvancedRetirementInputs,
  options: {
    runMonteCarlo?: boolean;
    monteCarloSimulations?: number;
    productBreakdowns?: ProductBreakdown[];
    customRiskProfiles?: RiskProfile[];
  } = {}
): ProjectionResult {
  const { runMonteCarlo = false, monteCarloSimulations = 500, productBreakdowns, customRiskProfiles = [] } = options;

  // Calculate starting portfolio
  const startingPortfolio = calculateStartingPortfolio(
    inputs.startPortfolioByCategory,
    inputs.includeNonLiquidRealestate,
    inputs.includeNonLiquidPension,
    productBreakdowns
  );

  // Run deterministic projection
  const { projection, fireYear, warnings, purchaseGoalsMet } = runDeterministicProjection(
    inputs,
    startingPortfolio,
    productBreakdowns
  );

  // Calculate FIRE metrics
  const annualSpend = inputs.monthlySpend * 12;
  const fireNumber = annualSpend / inputs.withdrawalRate;
  const currentYear = new Date().getFullYear();

  // Calculate weighted returns - use product-level if available for accuracy
  let weightedReturn: number;
  let weightedStdDev: number;
  
  if (productBreakdowns && productBreakdowns.length > 0) {
    // Initialize product balances to calculate accurate weighted returns
    const { spendableProducts } = initializeProductBalances(
      productBreakdowns, 
      inputs.includeNonLiquidRealestate, 
      inputs.includeNonLiquidPension
    );
    const productWeights = calculateWeightedReturnsFromProducts(
      spendableProducts,
      inputs.returnAssumptions,
      customRiskProfiles
    );
    weightedReturn = productWeights.weightedReturn;
    weightedStdDev = productWeights.weightedStdDev;
  } else {
    const categoryWeights = calculateWeightedReturns(
      inputs.startPortfolioByCategory,
      inputs.returnAssumptions,
      customRiskProfiles
    );
    weightedReturn = categoryWeights.weightedReturn;
    weightedStdDev = categoryWeights.weightedStdDev;
  }

  // Evaluate goals on deterministic projection
  const enabledGoals = (inputs.goals ?? []).filter(g => g.enabled);
  const deterministicGoalResults = enabledGoals.length > 0
    ? evaluateGoalsDeterministic(enabledGoals, projection, fireNumber, inputs.lifeExpectancy, purchaseGoalsMet)
    : undefined;

  const result: ProjectionResult = {
    deterministicProjection: projection,
    fireAchievedYear: fireYear,
    fireAchievedAge: fireYear ? inputs.currentAge + (fireYear - currentYear) : null,
    fireNumber,
    yearsToFire: fireYear ? fireYear - currentYear : null,
    warnings,
    weightedExpectedReturn: weightedReturn,
    weightedStandardDeviation: weightedStdDev,
    goalResults: deterministicGoalResults,
  };

  // Run Monte Carlo if requested
  if (runMonteCarlo) {
    result.monteCarlo = runMonteCarloSimulation(
      inputs,
      startingPortfolio,
      monteCarloSimulations,
      productBreakdowns,
      customRiskProfiles
    );

    // Merge MC goal success probabilities into deterministic goal results
    if (result.monteCarlo.goalResults && result.goalResults) {
      for (const mcGoal of result.monteCarlo.goalResults) {
        const detGoal = result.goalResults.find(g => g.goalId === mcGoal.goalId);
        if (detGoal) {
          mcGoal.deterministicMet = detGoal.deterministicMet;
          mcGoal.metAtAge = detGoal.metAtAge;
          // Also update the top-level goalResults with MC success probability
          detGoal.successProbability = mcGoal.successProbability;
        }
      }
    }
  }

  return result;
}
